import React, { useState, useEffect } from 'react';
import DashboardLayout from '../components/DashboardLayout';
import { Users, Trash2 } from 'lucide-react';
import { Button } from '../components/ui/button';
import { toast } from 'sonner';
import { supabase } from '../lib/supabase';
import { useAuth } from '../contexts/AuthContext';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '../components/ui/alert-dialog';

const AdminUsersPage = () => {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [deleteUser, setDeleteUser] = useState(null);
  const { user: currentUser, logAction } = useAuth();

  useEffect(() => {
    fetchUsers();
  }, []);

  const fetchUsers = async () => {
    try {
      const { data, error } = await supabase
        .from('profiles')
        .select('*')
        .order('created_at', { ascending: false });

      if (error) throw error;
      setUsers(data || []);
    } catch (error) {
      toast.error('Failed to fetch users');
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async () => {
    if (!deleteUser) return;
    try {
      const { error } = await supabase
        .from('profiles')
        .delete()
        .eq('id', deleteUser.id);

      if (error) throw error;

      setUsers(users.filter((u) => u.id !== deleteUser.id));
      await logAction(`Deleted user: ${deleteUser.email}`);
      toast.success('User removed successfully');
      setDeleteUser(null);
    } catch (error) {
      toast.error(error.message || 'Failed to delete user');
    }
  };

  return (
    <DashboardLayout>
      <div className="space-y-8">
        <div>
          <h1 className="text-2xl sm:text-3xl font-semibold tracking-tight" style={{ fontFamily: 'Outfit, sans-serif' }} data-testid="admin-users-title">
            User Management
          </h1>
          <p className="text-base text-muted-foreground mt-1">{users.length} registered users</p>
        </div>

        {loading ? (
          <div className="text-center py-12">
            <p className="text-muted-foreground font-mono">Loading users...</p>
          </div>
        ) : (
          <div className="bg-card border border-border rounded-lg overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-muted/50 border-b border-border">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs uppercase tracking-[0.2em] font-bold text-muted-foreground">Name</th>
                    <th className="px-6 py-3 text-left text-xs uppercase tracking-[0.2em] font-bold text-muted-foreground">Email</th>
                    <th className="px-6 py-3 text-left text-xs uppercase tracking-[0.2em] font-bold text-muted-foreground">Role</th>
                    <th className="px-6 py-3 text-left text-xs uppercase tracking-[0.2em] font-bold text-muted-foreground">Joined</th>
                    <th className="px-6 py-3 text-right text-xs uppercase tracking-[0.2em] font-bold text-muted-foreground">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-border">
                  {users.map((u) => (
                    <tr key={u.id} className="hover:bg-muted/30 transition-colors duration-150" data-testid="user-row">
                      <td className="px-6 py-4 text-sm font-medium">{u.name || 'N/A'}</td>
                      <td className="px-6 py-4 text-sm font-mono text-muted-foreground">{u.email}</td>
                      <td className="px-6 py-4">
                        <span className={`inline-block px-2 py-0.5 text-xs rounded ${
                          u.role === 'admin' ? 'bg-primary/10 text-primary' : 'bg-muted text-muted-foreground'
                        }`}>
                          {u.role}
                        </span>
                      </td>
                      <td className="px-6 py-4 text-sm font-mono text-muted-foreground">
                        {new Date(u.created_at).toLocaleDateString()}
                      </td>
                      <td className="px-6 py-4 text-right">
                        {u.id !== currentUser?.id && u.role !== 'admin' && (
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => setDeleteUser(u)}
                            className="text-destructive hover:text-destructive hover:bg-destructive/10"
                            data-testid="delete-user-btn"
                          >
                            <Trash2 className="w-4 h-4 mr-1" />Delete
                          </Button>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </div>

      <AlertDialog open={!!deleteUser} onOpenChange={() => setDeleteUser(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete User?</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to remove <strong>{deleteUser?.email}</strong> from the system?
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="delete-user-cancel-btn">Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={handleDelete} className="bg-destructive text-destructive-foreground hover:bg-destructive/90" data-testid="delete-user-confirm-btn">
              Delete User
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </DashboardLayout>
  );
};

export default AdminUsersPage;
